import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { isCapacitor } from '../../lib/platform';
import { t, getLang, setLang, SUPPORTED_LANGS } from '../../utils/creatorLang';
import {
  CreatorScreen, Pill, DiamondGem, I, Card,
} from '../../components/creator/primitives';

const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
};

const TEST_ACCOUNTS = [
  '7488811561440559121',
  '7449387418643628049',
  '7524033719896670224',
  '7553727804916039681',
  '7455676525237796881',
];

const LANG_ORDER = ['en', 'fr', 'it', 'de'];

const CreatorLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lang, setLangState] = useState(getLang());
  const isTestMode = searchParams.get('test') === 'true';
  const [testLogin, setTestLoginValue] = useState('');
  const [testPassword, setTestPassword] = useState('');

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && user.user_metadata?.role === 'creator') {
          // Inconnu (non rattaché à une agence) → espace public ; sinon portail agence.
          navigate(user.user_metadata?.known === false ? '/my/public-dashboard' : '/my/dashboard');
          return;
        }
        const legacy = localStorage.getItem('creator_session');
        if (legacy) localStorage.removeItem('creator_session');
      } catch {
        /* stay on login */
      }
    };
    checkSession();
  }, [navigate]);

  const cycleLang = () => {
    const idx = LANG_ORDER.indexOf(lang);
    const next = LANG_ORDER[(idx + 1) % LANG_ORDER.length];
    setLang(next);
    setLangState(next);
  };

  const handleTikTokLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const csrfState = generateCSRFToken();
      localStorage.setItem('tiktok_csrf_state', csrfState);

      // Mode sandbox de test : /my/login?sandbox=1 → clé+app sandbox (n'impacte pas le prod).
      const isSandbox = new URLSearchParams(window.location.search).get('sandbox') === '1';
      localStorage.setItem('tiktok_sandbox', isSandbox ? '1' : '');

      const clientKey = isSandbox
        ? import.meta.env.VITE_TIKTOK_CLIENT_KEY_SANDBOX
        : import.meta.env.VITE_TIKTOK_CLIENT_KEY;
      const redirectUri = import.meta.env.VITE_TIKTOK_REDIRECT_URI || 'https://noirock.app/auth/callback';

      if (!clientKey) {
        setError(t('login_err_config'));
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        client_key: clientKey,
        scope: 'user.info.basic,user.info.profile,user.info.stats,video.list',
        response_type: 'code',
        redirect_uri: redirectUri,
        state: csrfState,
      });

      const url = `https://www.tiktok.com/v2/auth/authorize/?${params.toString()}`;

      if (isCapacitor()) {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url });
      } else {
        window.location.href = url;
      }
    } catch {
      setError(t('login_err_init'));
      setLoading(false);
    }
  };

  const handleTestLogin = async () => {
    setLoading(true);
    setError('');
    try {
      if (!testLogin || !testPassword) {
        setError(t('login_err_empty'));
        setLoading(false);
        return;
      }
      if (testLogin !== testPassword) {
        setError(t('login_err_invalid'));
        setLoading(false);
        return;
      }
      if (!TEST_ACCOUNTS.includes(testLogin)) {
        setError(t('login_err_unauthorized'));
        setLoading(false);
        return;
      }

      const { data: creator, error: dbError } = await supabase
        .from('black_creators')
        .select('id, tiktok_id, tiktok_username, tiktok_display_name, tiktok_avatar_url, tiktok_open_id')
        .eq('tiktok_id', testLogin)
        .single();

      if (dbError || !creator) {
        setError(t('login_err_not_found'));
        setLoading(false);
        return;
      }

      const { data: authData, error: authError } = await supabase.functions.invoke('tiktok-auth', {
        body: { test_login: creator.tiktok_id },
      });

      if (authError || !authData?.success || !authData?.session) {
        setError(t('login_err_session'));
        setLoading(false);
        return;
      }

      await supabase.auth.setSession({
        access_token: authData.session.access_token,
        refresh_token: authData.session.refresh_token,
      });

      localStorage.setItem('creator_profile', JSON.stringify({
        tiktok_id: creator.tiktok_id,
        username: creator.tiktok_username,
        display_name: creator.tiktok_display_name,
        avatar_url: creator.tiktok_avatar_url,
      }));

      navigate('/my/dashboard');
    } catch (err) {
      console.error('Test login error:', err);
      setError(t('login_err_generic'));
      setLoading(false);
    }
  };

  return (
    <CreatorScreen>
      <div className="grain" style={{
        position: 'relative', minHeight: '100dvh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        padding: '48px 24px 32px',
        paddingTop: 'calc(48px + env(safe-area-inset-top, 0px))',
      }}>
        {/* Ambient glows */}
        <div style={{
          position: 'absolute', top: -120, right: -80, width: 300, height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, oklch(0.68 0.22 10 / 0.35) 0%, transparent 70%)',
          filter: 'blur(20px)', pointerEvents: 'none',
        }}/>
        <div style={{
          position: 'absolute', bottom: 180, left: -100, width: 280, height: 280,
          borderRadius: '50%',
          background: 'radial-gradient(circle, oklch(0.78 0.16 210 / 0.25) 0%, transparent 70%)',
          filter: 'blur(20px)', pointerEvents: 'none',
        }}/>

        {/* Top: logo + lang */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'relative', zIndex: 2,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #fff 0%, #aaa 100%)',
              display: 'grid', placeItems: 'center',
              color: '#000', fontWeight: 800, fontSize: 18, letterSpacing: -1,
              fontFamily: 'var(--font-mono)',
            }}>N</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.3, lineHeight: 1, color: 'var(--fg-0)' }}>NOIROCK</div>
              <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }}>Creator Space</div>
            </div>
          </div>
          <div className="tap" onClick={cycleLang} style={{
            padding: '6px 10px', borderRadius: 999,
            background: 'var(--bg-3)', border: '1px solid var(--line-1)',
            fontSize: 11, fontWeight: 600, color: 'var(--fg-1)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <I.Globe size={12} stroke="var(--fg-1)"/>
            {lang.toUpperCase()}
          </div>
        </div>

        {/* Hero */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', zIndex: 2, paddingTop: 40 }}>
          <Pill color="pink" style={{ alignSelf: 'flex-start', marginBottom: 20 }}>
            <span className="pulse-soft" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-pink)', display: 'inline-block' }}/>
            {t('login_sub')}
          </Pill>
          <h1 style={{
            margin: 0, fontSize: 44, lineHeight: 1.05, letterSpacing: -1.6,
            fontWeight: 800, whiteSpace: 'pre-line', color: 'var(--fg-0)',
          }}>{t('login_title')}</h1>

          <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { icon: <DiamondGem size={16} color="var(--accent-cyan)"/>, text: t('login_bullet_1') },
              { icon: <I.Cash size={16} stroke="var(--accent-green)"/>, text: t('login_bullet_2') },
              { icon: <I.Shield size={16} stroke="var(--accent-gold)"/>, text: t('login_bullet_3') },
            ].map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 10,
                  background: 'var(--bg-3)', border: '1px solid var(--line-1)',
                  display: 'grid', placeItems: 'center',
                }}>{b.icon}</div>
                <span style={{ fontSize: 14, color: 'var(--fg-1)', fontWeight: 500 }}>{b.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA / forms */}
        <div style={{ position: 'relative', zIndex: 2, marginTop: 24 }}>
          {error && (
            <div style={{
              marginBottom: 14, padding: '10px 14px', borderRadius: 12,
              background: 'var(--accent-red-soft)',
              border: '1px solid oklch(0.65 0.24 25 / 0.3)',
              color: 'var(--accent-red)', fontSize: 13, fontWeight: 500,
            }}>{error}</div>
          )}

          {!isTestMode ? (
            <>
              <button onClick={handleTikTokLogin} disabled={loading} className="tap" style={{
                width: '100%', height: 56,
                background: loading ? 'var(--bg-3)' : 'var(--fg-0)',
                color: loading ? 'var(--fg-3)' : 'var(--bg-0)',
                border: 'none', borderRadius: 16,
                fontSize: 16, fontWeight: 700, letterSpacing: -0.2,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 8px 24px rgba(255,255,255,0.15)',
                fontFamily: 'var(--font-sans)',
              }}>
                <I.Tiktok size={20} stroke={loading ? 'var(--fg-3)' : 'var(--bg-0)'}/>
                {loading ? t('login_cta_loading') : t('login_cta')}
              </button>

              <div style={{
                marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                fontSize: 11, color: 'var(--fg-3)',
              }}>
                <I.Shield size={12} stroke="var(--accent-gold)"/>
                <span>{t('login_agency')}</span>
              </div>

              <div style={{
                marginTop: 12, fontSize: 11, color: 'var(--fg-3)',
                textAlign: 'center', lineHeight: 1.5,
              }}>
                {t('login_tos')}{' '}
                <a href="/terms.html" style={{ color: 'var(--fg-1)', textDecoration: 'underline' }}>{t('login_tos_link')}</a>
                {' '}{t('login_tos_and')}{' '}
                <a href="/privacy.html" style={{ color: 'var(--fg-1)', textDecoration: 'underline' }}>{t('login_priv_link')}</a>
              </div>

              <div style={{
                marginTop: 20, fontSize: 11, color: 'var(--fg-3)', textAlign: 'center',
                lineHeight: 1.55, padding: '0 8px',
              }}>{t('login_trust')}</div>
            </>
          ) : (
            <Card pad={18} style={{ background: 'var(--bg-2)' }}>
              <div style={{ fontSize: 10, color: 'var(--fg-3)', textAlign: 'center', marginBottom: 14, lineHeight: 1.5 }}>
                {t('login_test_hint')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--fg-3)', marginBottom: 6, letterSpacing: 0.3, textTransform: 'uppercase', fontWeight: 600 }}>{t('login_test_login')}</div>
                  <input
                    value={testLogin}
                    onChange={e => setTestLoginValue(e.target.value.trim())}
                    placeholder="tiktok_id"
                    style={{
                      width: '100%', height: 42, borderRadius: 12, padding: '0 14px',
                      background: 'var(--bg-3)', border: '1px solid var(--line-2)',
                      color: 'var(--fg-0)', fontSize: 16, fontFamily: 'var(--font-mono)',
                      outline: 'none',
                    }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--fg-3)', marginBottom: 6, letterSpacing: 0.3, textTransform: 'uppercase', fontWeight: 600 }}>{t('login_test_password')}</div>
                  <input
                    type="password"
                    value={testPassword}
                    onChange={e => setTestPassword(e.target.value.trim())}
                    onKeyDown={e => e.key === 'Enter' && handleTestLogin()}
                    placeholder="••••••••"
                    style={{
                      width: '100%', height: 42, borderRadius: 12, padding: '0 14px',
                      background: 'var(--bg-3)', border: '1px solid var(--line-2)',
                      color: 'var(--fg-0)', fontSize: 16, fontFamily: 'var(--font-mono)',
                      outline: 'none',
                    }}
                  />
                </div>
                <button onClick={handleTestLogin} disabled={loading} className="tap" style={{
                  height: 46, marginTop: 4,
                  background: loading ? 'var(--bg-3)' : 'var(--accent-pink)',
                  color: loading ? 'var(--fg-3)' : '#fff',
                  border: 'none', borderRadius: 12,
                  fontSize: 14, fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-sans)',
                }}>
                  {loading ? t('login_test_loading') : t('login_test_go')}
                </button>
              </div>
            </Card>
          )}

          {/* Manager/Admin link */}
          <div style={{
            marginTop: 18, textAlign: 'center', fontSize: 11, color: 'var(--fg-3)',
          }}>
            <Link to="/login-authentication" style={{ color: 'var(--fg-3)', textDecoration: 'none' }}>
              {t('login_manager_link')} →
            </Link>
          </div>

          <div style={{ textAlign: 'center', fontSize: 10, color: 'var(--fg-4)', marginTop: 12 }}>
            NoiRock v2.5 · NoiRock
          </div>
        </div>
      </div>
    </CreatorScreen>
  );
};

export default CreatorLogin;
